import React from 'react'
import { useNodes } from '@xyflow/react'

import { IActionType, VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import { ActionDataType } from '@/shared/action.types'

import { WorkflowNode } from '../types'

// Determine whether the provided settings contain any ActionOutput references
// that point to a non-existent or non-output-capable node in the current graph.
//
// Returns true when an invalid reference is found; otherwise false.
export default function useInvalidActionOutputReference(settings: any): boolean {
  const nodes = useNodes<WorkflowNode>()

  return React.useMemo(() => {
    if (!settings) return false

    // 1) Gather ids of nodes that can produce outputs (eligible reference targets)
    const OUTPUT_NODE_TYPES = [IActionType.TemporaryVariable, IActionType.FilterData, IActionType.FormAddRow]
    const outputNodeIds = new Set(
      nodes.filter((n) => OUTPUT_NODE_TYPES.includes(n.data.actionValue.type)).map((n) => n.data.actionValue.id)
    )

    // 2) Recognize ActionOutput reference blocks in two shapes:
    //    - ActionInput style (dataType === ActionOutput)
    //    - VariableText (Slate) style (type/flag === VariableTextFeatureFlag.ActionOutput)
    const isActionOutputBlock = (obj: any) => {
      if (!obj || typeof obj !== 'object') return false
      if (obj.dataType === ActionDataType.ActionOutput) return true
      if (obj.type === VariableTextFeatureFlag.ActionOutput) return true
      if (obj.flag === VariableTextFeatureFlag.ActionOutput) return true
      return false
    }

    // 3) Extract the referenced action id from a recognized ActionOutput block
    const getReferencedActionId = (block: any): string | undefined => {
      if (typeof block?.actionId === 'string') return block.actionId
      if (typeof block?.id === 'string') return block.id

      const value = block?.value
      if (typeof value === 'string') return value
      if (value && typeof value === 'object') return value.actionId || value.id

      return undefined
    }

    // 4) Walk the settings object to collect all referenced action ids
    const collectReferencedIds = (val: any, ids: Set<string>) => {
      if (val == null) return
      if (Array.isArray(val)) {
        for (const item of val) collectReferencedIds(item, ids)
        return
      }
      if (typeof val !== 'object') return

      if (isActionOutputBlock(val)) {
        const id = getReferencedActionId(val)
        if (id) ids.add(id)
      }

      for (const child of Object.values(val)) collectReferencedIds(child, ids)
    }

    const referencedIds = new Set<string>()
    collectReferencedIds(settings, referencedIds)

    // 5) Validate: any referenced id that isn't among output-capable nodes is invalid
    if (referencedIds.size === 0) return false

    for (const refId of referencedIds) {
      if (!outputNodeIds.has(refId)) return true
    }
    return false
  }, [settings, nodes])
}
